import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { db, eq } from "../../lib/api";
import { invalidate, useQuery } from "../../lib/hooks";
import { AREA_LABEL, useRefData, type Area } from "../../lib/refdata";
import { useSession } from "../../lib/session";
import { PageHead } from "../../components/Shell";
import { ErrorBox, Panel, Select, useToast } from "../../components/ui";

/** Settings → Phone contacts: Google Contacts → CRM, by label or #tag. */
type Rule = { match: string; area: Area | "" };
const AREAS: Area[] = ["new_connections", "networking", "hnw", "warm", "travel"];

export default function PhoneContacts() {
  const { profile, can } = useSession();
  const ref = useRefData();
  const toast = useToast();
  const boss = profile?.role_key === "owner" || profile?.role_key === "admin";
  const st = ref.data?.setting("phone_contacts") ?? {};
  const saved: Rule[] = ref.data?.setting("phone_contact_rules") ?? [];
  const [rules, setRules] = useState<Rule[] | null>(null);
  useEffect(() => { if (ref.data && rules === null) setRules(saved.length ? saved : [{ match: "", area: "" }]); }, [ref.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const labels = useQuery(st.connected ? "contacts:labels" : null, () => db.rpc<{ name: string; count: number }[]>("contacts_labels"), { staleMs: 120_000 });
  const [err, setErr] = useState<Error | null>(null);
  const [busy, setBusy] = useState("");
  const list: Rule[] = rules && rules.length ? rules : [{ match: "", area: "" }];
  const set = (i: number, p: Partial<Rule>) => setRules(list.map((r, j) => (j === i ? { ...r, ...p } : r)));

  async function connect() {
    setErr(null);
    try {
      const r = await db.rpc<{ ok: boolean; url?: string; reason?: string }>("contacts_auth_url");
      if (!r.ok || !r.url) throw new Error(r.reason ?? "Couldn't start the Google connection");
      window.location.href = r.url;
    } catch (e) { setErr(e as Error); }
  }
  async function save() {
    setErr(null);
    const clean = list.map((r) => ({ match: r.match.trim(), area: r.area })).filter((r) => r.match && r.area);
    try {
      await db.update("settings", { key: eq("phone_contact_rules") }, { value: clean });
      invalidate("ref:"); toast("Rules saved"); setRules(clean.length ? clean : [{ match: "", area: "" }]);
    } catch (e) { setErr(e as Error); }
  }
  async function syncNow(full = false) {
    setBusy("sync"); setErr(null);
    try {
      const r = await db.rpc<any>("contacts_sync_now", { p_full: full });
      invalidate("ref:"); invalidate("people"); invalidate("today");
      if (r.ok === false) throw new Error(r.reason);
      toast(r.reason === "no rules yet" ? "Add a rule first" : `Read ${r.read ?? 0} contacts · ${r.new ?? 0} new · ${r.matched_existing ?? 0} already in the CRM · ${r.lists_added ?? 0} added to lists`);
    } catch (e) { setErr(e as Error); } finally { setBusy(""); }
  }
  async function disconnect() {
    try { await db.rpc("contacts_disconnect"); invalidate("ref:"); toast("Disconnected"); } catch (e) { setErr(e as Error); }
  }
  const last = st.last_result;

  return (
    <>
      <PageHead title="Phone contacts" sub="Save a contact on your phone, give it a CRM label (or a #tag in its notes), and it lands in the right list within 10 minutes. Contacts without one of your labels never come in." />
      <ErrorBox error={err} />
      <Panel title="1. Connect your Google Contacts">
        <div className="panel-body" style={{ display: "grid", gap: 8 }}>
          {st.connected
            ? <div><b>Connected</b>{st.account ? ` to ${st.account}` : ""}. {st.last_sync ? `Last checked ${dayjs(st.last_sync).format("MMM D, h:mm A")}` : "Not checked yet"}
                {last ? ` · last time: ${last.read != null ? `read ${last.read} contacts, ` : ""}${last.new} new, ${last.matched_existing} already in the CRM, ${last.lists_added} added to lists${last.ignored != null ? `, ${last.ignored} without one of your tags` : ""}.` : ""}
                {last?.with_notes != null && <div className="small muted">{last.with_notes} contacts had notes · #tags seen: {(last.tags_seen ?? []).length ? (last.tags_seen as string[]).map((t) => "#" + t).join(", ") : "none"}</div>}</div>
            : <div className="small muted">Your phone must save new contacts to your Google account (iPhone: Settings → Contacts → Default Account → Gmail). Use the Google account those contacts are in; it can be different from the calendar one.</div>}
          {st.error && st.connected && <div className="small" style={{ color: "var(--alert)" }}>Google: {String(st.error).slice(0, 180)}</div>}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {boss && !st.connected && <button className="btn primary" onClick={connect}>Connect Google Contacts</button>}
            {st.connected && can("leads", "edit") && <button className="btn" disabled={!!busy} onClick={() => syncNow()}>{busy ? "Checking…" : "Check now"}</button>}
            {st.connected && boss && <button className="btn ghost" onClick={() => syncNow(true)} disabled={!!busy}>Re-check all contacts</button>}
            {st.connected && boss && <button className="btn ghost" onClick={disconnect}>Disconnect</button>}
          </div>
          {!st.connected && boss && <p className="small muted" style={{ margin: 0 }}>First time only: in Google Cloud (the same project as the calendar connection), turn on the <b>People API</b> (APIs & Services → Library → People API → Enable).</p>}
        </div>
      </Panel>
      <Panel title="2. Your rules: which label goes to which list" className="mt16"
        actions={can("settings", "edit") && <button className="btn small primary" onClick={save}>Save rules</button>}>
        <div className="panel-body" style={{ display: "grid", gap: 8 }}>
          {list.map((r, i) => (
            <div key={i} className="stage-row">
              <input className="input" list="contact-labels" value={r.match} onChange={(e) => set(i, { match: e.target.value })} placeholder="Your tag, e.g. HNW (matches #HNW in notes or the label HNW)" disabled={!can("settings", "edit")} />
              <span className="small muted">→</span>
              <Select value={r.area} onChange={(v) => set(i, { area: (v || "") as Area })} options={AREAS.map((a) => ({ value: a, label: AREA_LABEL[a] }))} placeholder="Add to list…" style={{ width: 200 }} />
              {can("settings", "edit") && <button className="btn small ghost" onClick={() => setRules(list.filter((_, j) => j !== i))}>Remove</button>}
            </div>))}
          <datalist id="contact-labels">{(labels.data ?? []).map((l) => <option key={l.name} value={l.name}>{`${l.count} contacts`}</option>)}</datalist>
          {can("settings", "edit") && <div><button className="btn small" onClick={() => setRules([...list, { match: "", area: "" }])}>+ Add a rule</button></div>}
          {st.connected && (labels.data ?? []).length > 0 && <p className="small muted" style={{ margin: 0 }}>Your Google labels: {(labels.data ?? []).map((l) => `${l.name} (${l.count})`).join(", ")}</p>}
          <p className="small muted" style={{ margin: 0 }}>“Met in person / at an event” sets today as the day you met, starts the 48-hour follow-up, and links today's event if there is one. Someone already in the CRM (same email or phone) just gets the new list — no duplicates.</p>
        </div>
      </Panel>
      <Panel title="How to tag a contact" className="mt16">
        <ul className="qa-steps">
          <li><b>On iPhone:</b> iPhone can't set Google labels, so type your tag with a <b>#</b> in the contact's <b>Notes</b> (e.g. <code>#HNW met at church</code>). A rule written as <b>HNW</b> or <b>#HNW</b> works the same; capitals don't matter. The tag is removed when it reaches the CRM; the rest becomes their notes.</li>
          <li><b>Labels:</b> on Android in the Contacts app, or on any phone at <b>contacts.google.com</b> → open the contact → <b>Manage labels</b>.</li>
          <li>It comes in within 10 minutes (or tap <b>Check now</b>). Adding another label or tag later adds them to that list too.</li>
        </ul>
      </Panel>
    </>
  );
}
